import React from 'react';
import { createPortal } from 'react-dom';
import pt from 'prop-types';
import cx from 'classnames';

import { storefrontThemeTemplateSelectOptions } from 'utils/storefront';

import Tabs from 'components/tabs';
import LocaleSelector from 'components/locale/selector';
import ThemePresetStyles from 'components/storefront/editor/theme-preset-styles';
import GlobalCSSWidget from 'components/storefront/editor/global-css-widget';
import SwellSelect from 'components/storefront/editor/widgets/select';
import {
  SWELL_GLOBAL,
  SWELL_PAGE_PREFIX,
  SWELL_DEFAULT,
  parseItemPath,
} from 'components/storefront/editor/utils/easyblocks';
import { postEasyblocksFocus } from 'components/storefront/editor/utils/post-message';
import { EasyblocksBackendContext } from 'components/storefront/editor/backendContext';

const SIDEBAR_ROOT_ID = 'sidebar-panels-root';
const FIELDS_GROUP_SELECTOR = 'div[class^="fields-builder__FieldsGroup"]';

const TABS = {
  THEME: { theme: { label: 'Theme' } },
  PAGE: { page: { label: 'Page' } },
  SECTION: { section: { label: 'Section' } },
  BLOCK: { block: { label: 'Block' } },
};

/** @param {React.KeyboardEvent} event */
function stopPropagation(event) {
  event.stopPropagation();
}

export default class EasyblocksTabs extends React.PureComponent {
  static contextType = EasyblocksBackendContext;

  static propTypes = {
    location: pt.object,
    focusedField: pt.string,
    page: pt.object,
    pageRecord: pt.object,
    primaryThemeTemplates: pt.object,

    onChangePageTemplate: pt.func,
  };

  state = {
    /** @type {HTMLDivElement | null} */
    tabsContainer: null,
  };

  /** @type {number} */
  sidebarMountInterval = 0;
  /** @type {AbortController | null} */
  abortController = null;

  componentDidMount() {
    this.createTabsContainer();
  }

  componentWillUnmount() {
    this.clearSidebarMountInterval();
    this.clearAbortController();

    const { tabsContainer } = this.state;

    if (tabsContainer) {
      tabsContainer.remove();
    }
  }

  clearSidebarMountInterval() {
    if (this.sidebarMountInterval) {
      clearInterval(this.sidebarMountInterval);
      this.sidebarMountInterval = 0;
    }
  }

  clearAbortController() {
    if (this.abortController !== null) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  createTabsContainer() {
    this.sidebarMountInterval = setInterval(() => {
      // Wait for the sidebar container to mount
      const sidebarContainer = document.getElementById(SIDEBAR_ROOT_ID);

      if (!sidebarContainer) {
        return;
      }

      this.clearAbortController();
      this.abortController = new AbortController();

      // Prevent events from propagating beyond sidebarContainer.
      // This is necessary so that keyboard events are not sent to move blocks in the editor.
      sidebarContainer.addEventListener('keydown', stopPropagation, {
        signal: this.abortController.signal,
      });

      // Stop checking once the sidebar container is found
      this.clearSidebarMountInterval();

      const tabsContainer = document.createElement('div');

      tabsContainer.classList.add(
        'storefront-editor--easyblocks--tabs-container',
      );

      // Insert tabs container as the first child
      sidebarContainer.prepend(tabsContainer);

      this.setState({ tabsContainer });
    }, 100);
  }

  onClickTab = (tab) => {
    const { focusedField } = this.props;
    const [sectionId] = parseItemPath(focusedField);

    switch (tab) {
      case 'theme':
        return postEasyblocksFocus(SWELL_GLOBAL);
      case 'page':
        return postEasyblocksFocus(SWELL_PAGE_PREFIX);
      case 'section':
        if (!sectionId) {
          return postEasyblocksFocus(SWELL_DEFAULT);
        }
        break;
      default:
        return;
    }
  };

  getTabs() {
    const { focusedField } = this.props;
    const [, blockId] = parseItemPath(focusedField);

    return {
      ...(blockId ? TABS.BLOCK : TABS.SECTION),
      ...TABS.PAGE,
      ...TABS.THEME,
    };
  }

  getActiveTab() {
    const { focusedField } = this.props;
    const [sectionId, blockId] = parseItemPath(focusedField);

    if (blockId) {
      return 'block';
    }

    if (sectionId || focusedField === SWELL_DEFAULT) {
      return 'section';
    }

    if (focusedField === SWELL_PAGE_PREFIX) {
      return 'page';
    }

    return 'theme';
  }

  renderOnTab(tab, render) {
    const { focusedField } = this.props;

    if (focusedField !== tab) {
      return null;
    }

    const fieldsGroupContainer = document.querySelector(FIELDS_GROUP_SELECTOR);

    if (!fieldsGroupContainer) {
      return null;
    }

    return createPortal(render(), fieldsGroupContainer);
  }

  renderDefaultTabContent() {
    return this.renderOnTab(SWELL_DEFAULT, () => (
      <div className="storefront-editor--easyblocks--default-tab-content">
        Select a section in the preview
        <br />
        to start customizing this page
      </div>
    ));
  }

  renderPageTabContent() {
    return this.renderOnTab(SWELL_PAGE_PREFIX, () => {
      const { page, pageRecord, primaryThemeTemplates, onChangePageTemplate } =
        this.props;
      const templates = primaryThemeTemplates[page.collection];

      if (!templates) {
        return null;
      }

      const field = {
        label: 'Template',
        description: 'Pick a record template',
        params: {
          options: storefrontThemeTemplateSelectOptions(templates.list),
        },
      };

      return (
        <div className="wrapFieldWithMeta__FieldWrapper">
          <SwellSelect
            value={pageRecord.theme_template || ''}
            field={field}
            onChange={onChangePageTemplate}
          />
        </div>
      );
    });
  }

  renderThemePresetsTabContent() {
    const { editorConfig } = this.context;

    // Only if the theme has preset styles
    if (!editorConfig.globals.configs.presets?.length) {
      return null;
    }

    return this.renderOnTab(SWELL_GLOBAL, () => (
      <ThemePresetStyles presets={editorConfig.globals.configs.presets} />
    ));
  }

  renderGlobalCSSWidget() {
    return this.renderOnTab(SWELL_GLOBAL, () => <GlobalCSSWidget />);
  }

  render() {
    const { location, focusedField } = this.props;
    const { tabsContainer } = this.state;

    if (!tabsContainer) {
      return null;
    }

    const activeTab = this.getActiveTab();
    const isDefaultTab = focusedField === SWELL_DEFAULT;

    return (
      <>
        {createPortal(
          <>
            <Tabs
              className={cx(
                'storefront-editor--easyblocks--tabs',
                `${activeTab}-tab`,
                {
                  'default-tab': isDefaultTab,
                },
              )}
              location={location}
              items={this.getTabs()}
              onClick={this.onClickTab}
              active={activeTab}
            />
            {!isDefaultTab && <LocaleSelector showCodesCount={false} />}
          </>,
          tabsContainer,
        )}
        {this.renderDefaultTabContent()}
        {this.renderPageTabContent()}
        {this.renderThemePresetsTabContent()}
        {this.renderGlobalCSSWidget()}
      </>
    );
  }
}
